/* Signature Parallax Media Banner - Parallax Engine (Couture)
   - 스크롤 위치에 따라 data-spmb-speed 값(px)을 기반으로 부드럽게 이동
   - prefers-reduced-motion 환경에서는 자동 비활성화
*/
(() => {
  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

  class SignatureParallaxMediaBanner extends HTMLElement {
    connectedCallback() {
      const enabled = String(this.dataset.parallaxEnabled) === "true";
      if (!enabled) return;

      // 접근성: 모션 최소화 설정 시 끔
      const reduceMotion = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (reduceMotion) return;

      this._layers = Array.from(this.querySelectorAll("[data-spmb-speed]"));
      if (!this._layers.length) return;

      this._ticking = false;
      this._onScroll = this._onScroll.bind(this);
      this._onResize = this._onResize.bind(this);
      this._update = this._update.bind(this);

      // 화면 근처에서만 활성화(성능)
      this._io = new IntersectionObserver(
        (entries) => {
          const isVisible = entries.some((e) => e.isIntersecting);
          if (isVisible) this._enable();
          else this._disable();
        },
        { root: null, threshold: 0, rootMargin: "200px 0px" }
      );
      this._io.observe(this);

      // 최초 1회
      this._enable();
    }

    disconnectedCallback() {
      this._disable(true);
    }

    _enable() {
      if (this._enabled) return;
      this._enabled = true;

      window.addEventListener("scroll", this._onScroll, { passive: true });
      window.addEventListener("resize", this._onResize, { passive: true });
      this._update();
    }

    _disable(force = false) {
      if (!this._enabled && !force) return;
      this._enabled = false;

      window.removeEventListener("scroll", this._onScroll);
      window.removeEventListener("resize", this._onResize);

      // 화면 밖일 때는 transform 정리(원치 않으면 주석처리 가능)
      for (const el of this._layers) el.style.transform = "";
      if (this._io && force) this._io.disconnect();
    }

    _onResize() {
      this._update();
    }

    _onScroll() {
      if (this._ticking) return;
      this._ticking = true;
      requestAnimationFrame(() => {
        this._ticking = false;
        this._update();
      });
    }

    _update() {
      if (!this.isConnected) return;

      const rect = this.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;

      // 섹션 중심이 뷰포트 중심에 가까울수록 0, 위/아래로 갈수록 -1~1
      const sectionCenter = rect.top + rect.height / 2;
      const viewportCenter = vh / 2;
      const denom = vh / 2 + rect.height / 2;

      let progress = 0;
      if (denom > 0) progress = (viewportCenter - sectionCenter) / denom;
      progress = clamp(progress, -1, 1);

      for (const el of this._layers) {
        const speed = parseFloat(el.dataset.spmbSpeed || "0");
        if (!speed) continue;
        const y = progress * speed; // px
        el.style.transform = `translate3d(0, ${y}px, 0)`;
      }
    }
  }

  if (!customElements.get("signature-parallax-media-banner")) {
    customElements.define("signature-parallax-media-banner", SignatureParallaxMediaBanner);
  }
})();
